import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useContent } from "../contexts/ContentContext";

const ReservationContext = createContext(null);

export function ReservationProvider({ children }) {
    const [open, setOpen] = useState(false);
    return (
        <ReservationContext.Provider value={{ open, setOpen }}>
            {children}
            <ReservationModal />
        </ReservationContext.Provider>
    );
}

export const useReservation = () => {
    const ctx = useContext(ReservationContext);
    if (!ctx) throw new Error("useReservation must be used inside ReservationProvider");
    return ctx;
};

function ReservationModal() {
    const { open, setOpen } = useReservation();
    const { content, t } = useContent();
    const url = content?.covermanager_url;
    const [preload, setPreload] = useState(false);
    const closeBtnRef = useRef(null);

    // Preconnect + warm up the iframe 2s after page load (after LCP)
    useEffect(() => {
        if (!url) return;
        let link;
        try {
            const origin = new URL(url).origin;
            link = document.createElement("link");
            link.rel = "preconnect";
            link.href = origin;
            link.crossOrigin = "anonymous";
            document.head.appendChild(link);
        } catch {
            /* noop */
        }
        const t = setTimeout(() => setPreload(true), 2000);
        return () => {
            clearTimeout(t);
            if (link && link.parentNode) link.parentNode.removeChild(link);
        };
    }, [url]);

    // Escape to close + body scroll lock
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        setTimeout(() => closeBtnRef.current?.focus(), 50);
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, setOpen]);

    if (!url) return null;

    return (
        <>
            {/* backdrop */}
            <div
                aria-hidden="true"
                onClick={() => setOpen(false)}
                className={`fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm transition-opacity duration-200 ${
                    open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
                }`}
            />
            {/* modal */}
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t?.nav?.reserve || "Reservar"}
                data-testid="reservation-dialog"
                className={`fixed z-[100] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[96vw] transition-all duration-200 ${
                    open ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
                }`}
                style={{
                    maxWidth: "42rem",
                    height: "min(780px, 94vh)",
                }}
            >
                <div className="bg-brand-cream rounded-2xl border border-brand-navy/15 overflow-hidden h-full flex flex-col shadow-2xl">
                    <div className="flex items-center justify-between px-5 sm:px-6 h-14 border-b border-brand-navy/10 bg-white shrink-0">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-brand-blue">Zarautz</div>
                            <div className="font-display text-lg text-brand-navy leading-none mt-0.5">
                                {t?.contact?.reserve_cta || t?.nav?.reserve || "Reservar"}
                            </div>
                        </div>
                        <button
                            ref={closeBtnRef}
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close"
                            className="p-2 rounded-full text-brand-navy hover:bg-brand-navy/5 transition-colors"
                            data-testid="reservation-close"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="relative flex-1 bg-white overflow-y-auto">
                        {preload ? (
                            <iframe
                                title="Zarautz reservations"
                                src={url}
                                className="block w-full bg-white"
                                style={{ height: "1150px", border: "0", marginTop: "-30px" }}
                                allow="payment"
                                data-testid="reservation-iframe"
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full">
                                <div className="w-8 h-8 rounded-full border-2 border-brand-navy/20 border-t-brand-terracotta animate-spin" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
